import { snakeToTitle } from "@/lib/utils";
import { ACTIVITY_TRANSLATION_KEYS } from "@/i18n/activityTranslationKeys";
import type { TranslationValues } from "use-intl/core";

export type Translator = {
  (key: string, values?: TranslationValues): string;
  has(key: string): boolean;
};

const ACTIVITY_TRANSLATION_KEY_SET = new Set<string>(ACTIVITY_TRANSLATION_KEYS);

const ACTIVITY_KEY_ALIASES: Record<string, string> = {
  table_tennis: "pingpong",
  dance: "dancing_and_cheer",
  canoeing: "water_sports",
  climbing: "other_sports",
  inclusive_sports: "other_sports",
  trampoline: "gymnastics",
  boxing: "martial_arts",
  karate: "martial_arts",
  judo: "martial_arts",
  taekwondo: "martial_arts",
};

function normalizeActivityKey(value: string): string {
  const normalized = value
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  return ACTIVITY_KEY_ALIASES[normalized] ?? normalized;
}

/**
 * Translate a dynamic key, falling back to a titleized version of the raw
 * value when the key is missing from the translation files. Check with
 * `t.has()` first: calling `t()` on a missing key reports MISSING_MESSAGE
 * through next-intl's `onError`, which the client provider logs.
 */
function translateOrFallback(
  t: Translator,
  key: string,
  rawValue: string,
): string {
  return t.has(key) ? t(key) : snakeToTitle(rawValue);
}

export function createTaxonomyFormatter(t: Translator) {
  return {
    activity: (value: string) => {
      const key = normalizeActivityKey(value);
      if (!ACTIVITY_TRANSLATION_KEY_SET.has(key)) {
        return snakeToTitle(value);
      }
      return t(`activities.${key}`);
    },
    ageGroup: (value: string) =>
      translateOrFallback(t, `quickFiltersOptions.ageGroup.${value}`, value),
    skillLevel: (value: string) =>
      translateOrFallback(t, `quickFiltersOptions.skillLevel.${value}`, value),
    clubType: (value: string) =>
      translateOrFallback(t, `quickFiltersOptions.clubType.${value}`, value),
    membership: (value: string) => {
      if (value === "isAcceptingSignups") {
        return t("quickFiltersOptions.membership.openForSignups");
      }
      return snakeToTitle(value);
    },
    membershipFee: (value: string) =>
      translateOrFallback(
        t,
        `quickFiltersOptions.membershipFee.${value}`,
        value,
      ),
    price: (value: string) =>
      translateOrFallback(t, `quickFiltersOptions.price.${value}`, value),
  } as const;
}
